import { QueryParams, useQueryParams } from 'client/routes/useQueryParams';
import { isEqual } from 'lodash';
import { useEffect, useState } from 'react';

/**
 * Convert an object to a JSON string.
 * @param {T} obj - T - The object to convert to a JSON string
 */
export const encodeObjectToString = <
  T extends Record<string | number, unknown>
>(
  obj: T
): string => JSON.stringify(obj);

/**
 * Converts the provided filters into query parameters.
 *
 * This function iterates over each key-value pair in the provided filters object.
 * If the value is undefined or an empty object, the key-value pair is ignored.
 * If the value is an object (and not empty), it is encoded into a string.
 * If the value is not an object, it is converted into a string.
 *
 * @template TFilter The type of the filters object.
 * @param {TFilter} filters The filters to convert into query parameters.
 * @returns {QueryParams} The converted query parameters.
 */
export const convertFiltersToQueryParams = <TFilter,>(
  filters: TFilter
): QueryParams =>
  Object.keys(filters).reduce((acc, key) => {
    const value = filters[key];
    if (
      value === undefined ||
      (typeof value === 'object' && Object.keys(value ?? []).length === 0)
    ) {
      return acc;
    }
    if (typeof value === 'object') {
      const encoded = encodeObjectToString(value);
      if (encoded !== '{}') {
        acc[key] = encoded;
      }
    } else {
      acc[key] = String(value);
    }
    return acc;
  }, {});

/**
 * "Convert a JSON string to an object."
 *
 * The function takes a string as an argument and returns an object
 * @param {string} value - The JSON string to convert to an object.
 */
export const decodeStringToObject = <T,>(value: string): T => {
  let res;
  try {
    res = JSON.parse(value);
  } catch (err) {
    res = value;
  }
  return res;
};

/**
 * It takes a filter object and updates the URL query params with the filter values
 * @param {TFilter} filters - TFilter - The filters object that you want to encode to the URL.
 */
export const useFiltersToUrlSearchParams = <TFilter,>(
  filters: TFilter
): void => {
  const { queryParams, updateQueryParams } = useQueryParams<QueryParams>();

  useEffect(() => {
    const updatedQueryParams: QueryParams =
      convertFiltersToQueryParams(filters);

    if (!isEqual(updatedQueryParams, queryParams)) {
      updateQueryParams(updatedQueryParams, true);
    }
  }, [filters]);
};

/**
 * It takes the query params from the URL and converts them into an object
 * @returns An object with a single property, initialFilters, which is a generic type.
 */
export const useFiltersFromUrlSearchParams = <TFilter,>(
  { updateOnChange } = { updateOnChange: false }
): {
  initialFilters: TFilter;
} => {
  const { queryParams } = useQueryParams<QueryParams>();
  const [initialFilters, setFilters] = useState<TFilter>(
    Object.keys(queryParams).reduce((acc, key) => {
      if (queryParams[key] === undefined || queryParams[key].length === 0) {
        return acc;
      }
      acc[key] = decodeStringToObject(queryParams[key]);
      return acc;
    }, {} as TFilter)
  );

  useEffect(() => {
    if (updateOnChange) {
      setFilters(
        Object.keys(queryParams).reduce((acc, key) => {
          if (queryParams[key] === undefined || queryParams[key].length === 0) {
            return acc;
          }
          acc[key] = decodeStringToObject(queryParams[key]);
          return acc;
        }, {} as TFilter)
      );
    }
  }, [queryParams]);

  return { initialFilters };
};
